/**
 * Maps ISO 3166-2 US subdivision codes (as returned by Vercel's edge geolocation
 * `countryRegion` field, e.g. "CA") to full state names.
 *
 * Builder.io copy interpolates `{{stateUSA}}` as the full name ("California"), and
 * the Feathery form seeds `initialValues.stateUSA` with it, so geo resolution must
 * convert Vercel's region code into the full name before it reaches the client.
 *
 * Anything not in this map (US territories, unknown codes, non-US) resolves to 'US'
 * via {@link stateCodeToName}.
 */
export const US_STATE_CODE_TO_NAME: Record<string, string> = {
	AL: 'Alabama',
	AK: 'Alaska',
	AZ: 'Arizona',
	AR: 'Arkansas',
	CA: 'California',
	CO: 'Colorado',
	CT: 'Connecticut',
	DE: 'Delaware',
	DC: 'District of Columbia',
	FL: 'Florida',
	GA: 'Georgia',
	HI: 'Hawaii',
	ID: 'Idaho',
	IL: 'Illinois',
	IN: 'Indiana',
	IA: 'Iowa',
	KS: 'Kansas',
	KY: 'Kentucky',
	LA: 'Louisiana',
	ME: 'Maine',
	MD: 'Maryland',
	MA: 'Massachusetts',
	MI: 'Michigan',
	MN: 'Minnesota',
	MS: 'Mississippi',
	MO: 'Missouri',
	MT: 'Montana',
	NE: 'Nebraska',
	NV: 'Nevada',
	NH: 'New Hampshire',
	NJ: 'New Jersey',
	NM: 'New Mexico',
	NY: 'New York',
	NC: 'North Carolina',
	ND: 'North Dakota',
	OH: 'Ohio',
	OK: 'Oklahoma',
	OR: 'Oregon',
	PA: 'Pennsylvania',
	RI: 'Rhode Island',
	SC: 'South Carolina',
	SD: 'South Dakota',
	TN: 'Tennessee',
	TX: 'Texas',
	UT: 'Utah',
	VT: 'Vermont',
	VA: 'Virginia',
	WA: 'Washington',
	WV: 'West Virginia',
	WI: 'Wisconsin',
	WY: 'Wyoming',
};

/** Default value used for non-US, unknown, or unresolved locations. */
export const DEFAULT_STATE_USA = 'US';

/**
 * Cookie set by `middleware.ts` (from Vercel edge geolocation) and read
 * synchronously client-side in `useGeoData`. Holds the resolved full state name.
 */
export const GEO_STATE_COOKIE = 'geo_state';

/**
 * Converts a Vercel region code into a full US state name, or 'US' when the
 * country is not US / the code is missing or unrecognized.
 */
export function stateCodeToName(
	regionCode: string | null | undefined,
	countryCode: string | null | undefined,
): string {
	if (countryCode && countryCode !== 'US') {
		return DEFAULT_STATE_USA;
	}
	if (!regionCode) {
		return DEFAULT_STATE_USA;
	}
	return US_STATE_CODE_TO_NAME[regionCode.toUpperCase()] ?? DEFAULT_STATE_USA;
}
